<script setup lang="ts">
import { type ComponentPublicInstance } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { formatRelativeTime, type AppTimeInput } from '~/utils/appTime'
import {
  type ExposureCategoryItem,
  type ExposureListItem,
  type ExposureRevelationPageData,
  type ExposureRevelationResponse,
  invokeExposureRoute,
  normalizeExposureRevelationData,
} from '~/api/exposure'

const REVELATION_PAGE_SIZE = 20
const EXPOSURE_SEARCH_QUERY_KEY = 'exposureKeyword'
const REVIEW_REVELATION_ROUTES_SSR = 'wikicommunity$fxnegativecommentpagelist,wikicommunity$fxgradenegativecomment'
const REVIEW_REVELATION_ROUTES_CLIENT = 'wikicommunity$fxnegativecommentpagelist'

const route = useRoute()
const nuxtApp = useNuxtApp()
const appStore = useAppStore()
const api = useEnvApi()
const { t } = useAppI18n()

const reviewsSeoTitle = computed(() => {
  const title = t('84440')
  return title.includes('WikiBit') ? title : `${title} | WikiBit`
})

/** 曝光频道统一 TDK，避免 8 个页面重复声明。 */
useHead(() => ({
  title: reviewsSeoTitle.value,
  meta: [
    {
      name: 'description',
      content: t('84439'),
    },
  ],
}))
const localePath = useLocalePath()
const matomo = useMatomo()
const { setBreadcrumb } = useLsBreadcrumb()

/** 从路由参数中提取纯数字页码。 */
const currentPage = computed(() => {
  const pageValue = route.params.page || route.params.slug
  const pageSlug = Array.isArray(pageValue) ? pageValue[0] : pageValue
  const parsedPage = Number(pageSlug || '1')

  if (!Number.isFinite(parsedPage) || parsedPage < 1) {
    return 1
  }

  return Math.trunc(parsedPage)
})

/** 二级 tab 分类缓存，useState 跨重挂载保持，首屏 SSR 写入。 */
const revelationCategories = useState<ExposureCategoryItem[]>('reviews-categories', () => [])

/** 从路由参数取原始 tab alias。 */
const resolveCategoryAlias = (): string => {
  const categoryParam = route.params.category
  const categoryAlias = Array.isArray(categoryParam) ? categoryParam[0] : categoryParam
  const trimmed = typeof categoryAlias === 'string' ? categoryAlias.trim() : ''

  return trimmed || '0'
}

/** 当前分类 code，点 tab 时立即赋值，路由变化时同步。 */
const resolveCategoryCodeFromAlias = (alias: string): string => {
  if (alias === '0') return '0'
  const matched = revelationCategories.value.find((item: ExposureCategoryItem) => item.alias === alias)
  return matched?.code || '0'
}

const currentCategoryCode = useState<string>(`reviews-current-category-code:${resolveCategoryAlias()}`, () =>
  resolveCategoryCodeFromAlias(resolveCategoryAlias()),
)
const currentRequestTime = useState<number>('reviews-current-request-time', () => Date.now())
const pendingCategoryCodeFromClick = ref('')
const secondaryTabRefs = ref<Record<string, HTMLElement>>({})

/** 缓存二级 tab DOM，点击后让横向滚动容器把目标 tab 滚入可视区域。 */
const setSecondaryTabRef = (element: Element | ComponentPublicInstance | null, tabKey: string) => {
  if (element instanceof HTMLElement) {
    secondaryTabRefs.value[tabKey] = element
    return
  }

  delete secondaryTabRefs.value[tabKey]
}

/** 使用浏览器原生 inline 对齐，自动兼容 RTL/LTR 横向滚动方向差异。 */
const scrollSecondaryTabIntoView = async (tabKey: string) => {
  await nextTick()
  secondaryTabRefs.value[tabKey]?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  })
}

/** 路由变化时重新解析 code。 */
watch(
  () => [route.params.category, route.query.c],
  () => {
    const next = resolveCategoryCodeFromAlias(resolveCategoryAlias())
    if (pendingCategoryCodeFromClick.value === next) {
      pendingCategoryCodeFromClick.value = ''
      return
    }

    if (next !== currentCategoryCode.value) {
      currentCategoryCode.value = next
    }
  },
)

/** URL 上的搜索词。 */
const currentKeyword = computed(() => {
  const searchQuery = route.query[EXPOSURE_SEARCH_QUERY_KEY]
  const resolvedKeyword = Array.isArray(searchQuery) ? searchQuery[0] : searchQuery

  return typeof resolvedKeyword === 'string' ? resolvedKeyword.trim() : ''
})

const EMPTY_REVELATION_PAGE_DATA: ExposureRevelationPageData = {
  categories: [],
  list: [],
  total: 0,
}

/** 发送曝光聚合请求，SSR 用双路由含分类，客户端只用列表路由。 */
const sendRevelationRequest = async (negativeType: string): Promise<ExposureRevelationPageData> => {
  const routes = import.meta.server ? REVIEW_REVELATION_ROUTES_SSR : REVIEW_REVELATION_ROUTES_CLIENT
  const response = await invokeExposureRoute<ExposureRevelationResponse>(
    routes,
    {
      userid: '',
      keyword: currentKeyword.value,
      pageIndex: currentPage.value,
      pageSize: REVELATION_PAGE_SIZE,
      negativeType,
    },
    api,
  )

  const normalized = normalizeExposureRevelationData(response)

  if (import.meta.client && normalized.categories.length === 0) {
    normalized.categories = revelationCategories.value
  }

  return normalized
}

/** 拉取曝光列表数据，SSR 首屏自动反查 code 后用正确 code 请求。 */
const fetchRevelationData = async (): Promise<ExposureRevelationPageData> => {
  try {
    // SSR 首屏：URL 有 category alias 时，先确保能拿到 code，再用 code 拉列表。
    if (import.meta.server && currentCategoryCode.value === '0') {
      const routeAlias = resolveCategoryAlias()
      if (routeAlias !== '0') {
        let firstResult: ExposureRevelationPageData | null = null
        if (revelationCategories.value.length === 0) {
          firstResult = await sendRevelationRequest('0')
          if (firstResult.categories.length > 0) {
            revelationCategories.value = firstResult.categories
          }
        }

        const resolvedCode = resolveCategoryCodeFromAlias(routeAlias)

        if (resolvedCode !== '0') {
          currentCategoryCode.value = resolvedCode
          const listResult = await nuxtApp.runWithContext(() => sendRevelationRequest(currentCategoryCode.value))
          return {
            ...listResult,
            categories: revelationCategories.value,
          }
        }

        if (firstResult) return firstResult
      }
    }

    return await sendRevelationRequest(currentCategoryCode.value)
  } catch {
    return EMPTY_REVELATION_PAGE_DATA
  }
}

const revelationAsyncDataKey = `reviews-exposure:${currentPage.value}:${resolveCategoryAlias()}:${currentKeyword.value}`
const { data: revelationPageData, pending } = await useAsyncData<ExposureRevelationPageData>(
  revelationAsyncDataKey,
  fetchRevelationData,
  {
    default: () => EMPTY_REVELATION_PAGE_DATA,
    watch: [currentPage, currentCategoryCode, currentKeyword],
  },
)

/** SSR 首屏数据到达后，写入分类缓存（仅一次）。 */
if (revelationCategories.value.length === 0 && revelationPageData.value.categories.length > 0) {
  revelationCategories.value = revelationPageData.value.categories
}

const resolvedCategoryCode = resolveCategoryCodeFromAlias(resolveCategoryAlias())
if (import.meta.server && resolvedCategoryCode !== currentCategoryCode.value) {
  currentCategoryCode.value = resolvedCategoryCode
}

/** 最新曝光二级 tab，补上"全部"后按分类缓存渲染。 */
const secondaryTabs = computed(() => {
  return [
    { key: '0', label: t('83794') },
    ...revelationCategories.value.map((item: ExposureCategoryItem) => ({
      key: `${item.code || ''}`,
      alias: item.alias || '',
      label: item.name || '',
    })),
  ].filter((item) => item.label)
})

/** 当前选中 tab 的 URL 标识，用于分页路由生成。 */
const currentCategoryAlias = computed(() => {
  if (currentCategoryCode.value === '0') return ''
  const tab = secondaryTabs.value.find((item) => item.key === currentCategoryCode.value)
  return tab?.alias
})

/** 分页最大页数。 */
const revelationMaxPage = computed(() => {
  const total = Number(revelationPageData.value?.total || 0)
  return Math.max(1, Math.ceil(total / REVELATION_PAGE_SIZE))
})

/** 仅在数据加载完成后展示分页，避免切换 tab 时出现旧分页。 */
const showRevelationPagination = computed(() => !pending.value && revelationMaxPage.value > 1)

/** 分页路由模板，按分类和搜索状态生成。 */
const revelationPageRoute = computed(() => {
  const query = new URLSearchParams()

  if (currentKeyword.value) {
    query.set(EXPOSURE_SEARCH_QUERY_KEY, currentKeyword.value)
  }

  const hasSearch = currentKeyword.value !== ''
  let basePath = ''

  if (currentCategoryAlias.value) {
    basePath = hasSearch
      ? `/reviews/category/${currentCategoryAlias.value}/page/{idx}/search`
      : `/reviews/category/${currentCategoryAlias.value}/page/{idx}`
  } else {
    basePath = hasSearch ? `/reviews/page/{idx}/search` : '/reviews/page/{idx}'
  }

  const queryString = query.toString()
  return `${basePath}${queryString ? `?${queryString}` : ''}`
})

const getExposureImage = (item: ExposureListItem) => item.images?.[0]?.detail || item.images?.[0]?.list || ''

/** 曝光列表时间展示。 */
const appTime = (timestamp: AppTimeInput) =>
  formatRelativeTime(timestamp, appStore.LanguageCode || 'en', {
    now: currentRequestTime.value,
  })

const stripHtml = (text: string) =>
  text
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/** 点击 tab 立即更新 code，再改 URL。 */
const navigateToCategory = async (tabKey: string) => {
  const tabItem = secondaryTabs.value.find((item) => item.key === tabKey)
  if (!tabItem) return

  pendingCategoryCodeFromClick.value = tabKey
  currentCategoryCode.value = tabKey
  await scrollSecondaryTabIntoView(tabKey)

  matomo.trackEvent('af_Exposure_Tab', {
    af_type: tabItem.label,
    af_code: tabKey,
  })

  const query: Record<string, string> = {}
  const hasSearch = currentKeyword.value !== ''

  if (hasSearch) {
    query[EXPOSURE_SEARCH_QUERY_KEY] = currentKeyword.value
  }

  let targetPath = ''
  if (tabKey === '0') {
    targetPath = hasSearch ? '/reviews/search' : '/reviews'
  } else {
    const categoryAlias = tabItem.alias
    targetPath = hasSearch ? `/reviews/category/${categoryAlias}/search` : `/reviews/category/${categoryAlias}`
  }

  await navigateTo(
    {
      path: localePath(targetPath),
      query,
    },
    { replace: true },
  )
}

/** 缓存反馈详情页的列表来源面包屑。 */
const setFeedbackDetailBreadcrumb = (item: ExposureListItem) => {
  setBreadcrumb(item.commentGradeId, {
    route: route.fullPath,
    labelKey: '82430',
  })
}

/** 转义 HTML。 */
const escapeHtml = (text: string) => {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

/** 转义正则关键字符。 */
const escapeRegExp = (text: string) => {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** 根据搜索词高亮命中文本，按完整关键词匹配而不是按单字符拆分。 */
const getHighlightedSummary = (item: ExposureListItem) => {
  const keyword = currentKeyword.value.trim()
  const escapedSummary = escapeHtml(`${stripHtml(item.content || '')}`.trim())

  if (!keyword) {
    return escapedSummary
  }

  const keywordSegments = keyword
    .split(/\s+/)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => escapeRegExp(segment))

  if (!keywordSegments.length) {
    return escapedSummary
  }

  const pattern = new RegExp(`(${keywordSegments.join('|')})`, 'gi')
  return escapedSummary.replace(pattern, '<span class="text-[#f04438]">$1</span>')
}

const trackExposureDetail = (item: ExposureListItem) => {
  const currentTabKey = secondaryTabs.value.find((tab) => tab.key === currentCategoryCode.value)?.key || ''

  matomo.trackEvent('af_Exposure_Details', {
    af_source: '曝光列表',
    af_code: item.commentGradeId || '',
    af_content: item.traderCode || '',
    af_name: item.traderName || '',
    af_type: item.negativeType || '',
    af_type1: currentTabKey,
  })
}

const handleClick = (item: ExposureListItem) => {
  trackExposureDetail(item)
  setFeedbackDetailBreadcrumb(item)
  trackExposureLeave()
}

/** 曝光列表停留时长埋点。 */
let exposureEnterTime = 0
let hasTrackedExposureLeave = false

/** 记录曝光列表离开时间，避免路由离开和页面卸载重复上报。 */
const trackExposureLeave = () => {
  if (hasTrackedExposureLeave) {
    return
  }

  hasTrackedExposureLeave = true
  matomo.trackEvent('af_Exposure_List_Time', {
    af_Enter: exposureEnterTime,
    af_Exit: Date.now(),
  })
}

onMounted(() => {
  exposureEnterTime = Date.now()
  matomo.trackEvent('af_Exposure_List_Time', {
    af_Enter: exposureEnterTime,
  })
  window.addEventListener('pagehide', trackExposureLeave)
})

onBeforeRouteLeave(() => {
  trackExposureLeave()
})

onBeforeUnmount(() => {
  window.removeEventListener('pagehide', trackExposureLeave)
  trackExposureLeave()
})
</script>

<template>
  <section class="flex flex-col">
    <div
      v-if="!currentKeyword && secondaryTabs.length > 0"
      class="overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <div class="flex w-max min-w-full flex-nowrap gap-[8px] pb-[18px]">
        <button
          v-for="item in secondaryTabs"
          :key="item.key"
          :ref="(element) => setSecondaryTabRef(element, item.key)"
          type="button"
          class="flex h-[36px] shrink-0 cursor-pointer items-center rounded-[2px] border px-[16px] text-[16px] leading-[24px] transition-colors"
          :class="
            item.key === currentCategoryCode
              ? 'border-[#1e3a8a] bg-[#1e3a8a] font-[500] text-[#ffffff]'
              : 'border-[#e5e7eb] bg-[#ffffff] text-[#4b5563] hover:border-[#d1d5db] hover:text-[#292a2e]'
          "
          @click="navigateToCategory(item.key)"
        >
          {{ item.label }}
        </button>
      </div>
    </div>

    <div
      v-if="pending"
      class="flex flex-col"
      role="status"
      aria-live="polite"
      aria-label="Loading exposure list"
      aria-busy="true"
    >
      <div
        v-for="item in 6"
        :key="`review-exposure-skeleton-${item}`"
        class="grid grid-cols-[minmax(0,1fr)_192px] gap-[16px] border-b border-[rgba(5,21,36,0.06)] px-[16px] py-[24px] last:border-b-0"
        aria-hidden="true"
      >
        <div class="flex min-w-0 flex-col justify-between">
          <div class="flex flex-col gap-[8px]">
            <span class="h-[16px] w-full max-w-[560px] animate-pulse rounded-[4px] bg-[#f1f2f4]"></span>
            <span class="h-[16px] w-[88%] max-w-[500px] animate-pulse rounded-[4px] bg-[#f1f2f4]"></span>
            <span class="h-[16px] w-[64%] max-w-[360px] animate-pulse rounded-[4px] bg-[#f1f2f4]"></span>
          </div>

          <div class="mt-[12px] flex items-center justify-between gap-[12px]">
            <div class="flex min-w-0 items-center gap-[8px]">
              <span class="size-[20px] shrink-0 animate-pulse rounded-[2px] bg-[#f1f2f4]"></span>
              <span class="h-[16px] w-[96px] animate-pulse rounded-[4px] bg-[#f1f2f4]"></span>
              <span class="h-[20px] w-[56px] animate-pulse rounded-[2px] bg-[#f1f2f4]"></span>
            </div>
            <span class="h-[16px] w-[92px] shrink-0 animate-pulse rounded-[4px] bg-[#f1f2f4]"></span>
          </div>
        </div>

        <span class="h-[108px] animate-pulse rounded-[8px] bg-[#f1f2f4]"></span>
      </div>
    </div>

    <div v-else-if="(revelationPageData?.list || []).length > 0" class="flex flex-col">
      <NuxtLink
        v-for="(item, index) in revelationPageData.list"
        :key="item.commentGradeId || `review-exposure-${index + 1}`"
        :to="localePath(`/reviews/${item.commentGradeId}`)"
        target="_blank"
        class="group block cursor-pointer px-[16px] transition-colors hover:rounded-[12px] hover:bg-[#f8f8f8] last:[&_.a-list]:border-0"
        @click="handleClick(item)"
      >
        <div
          class="a-list grid gap-[16px] border-b border-[rgba(5,21,36,0.06)] py-[24px]"
          :class="getExposureImage(item) ? 'grid-cols-[minmax(0,1fr)_192px]' : 'grid-cols-[minmax(0,1fr)]'"
        >
          <div class="flex min-w-0 flex-col justify-between">
            <p
              class="line-clamp-3 text-[16px] leading-[24px] [overflow-wrap:anywhere] [word-break:normal] text-[#292a2e]"
              v-html="getHighlightedSummary(item)"
            ></p>

            <div class="mt-[12px] flex items-center justify-between gap-[12px]">
              <div class="flex min-w-0 items-center gap-[8px]">
                <div class="flex min-w-0 items-center gap-[4px]">
                  <CommonImage
                    class="size-[20px] shrink-0 rounded-[2px] object-cover"
                    :src="item.traderIcon || ''"
                    alt=""
                    loading="eager"
                    object-fit="cover"
                  />
                  <span class="truncate text-[16px] leading-[24px] text-[#292a2e]">{{ item.traderName || '' }}</span>
                </div>
                <span
                  class="rounded-[2px] bg-[#f8f8f8] px-[8px] py-[2px] text-[11px] leading-[16px] text-[#505258] group-hover:bg-[#ffffff]"
                >
                  {{ item.negativeType || '--' }}
                </span>
              </div>

              <div class="shrink-0 text-[13px] leading-[20px] text-[#8c8f97]">
                {{ item.country || '' }} {{ appTime(item.publishTime) }}
              </div>
            </div>
          </div>

          <div
            v-if="getExposureImage(item)"
            class="h-[108px] overflow-hidden rounded-[8px] border-[0.5px] border-[rgba(5,21,36,0.06)]"
          >
            <CommonImage
              class="size-full object-cover"
              :src="getExposureImage(item)"
              alt=""
              loading="eager"
              object-fit="cover"
            />
          </div>
        </div>
      </NuxtLink>
    </div>
    <CommonEmptyState v-else />

    <div v-if="showRevelationPagination" class="mt-[36px] flex justify-center">
      <CommonPagination
        :current-page="currentPage"
        :total="revelationPageData?.total || 0"
        :page-size="REVELATION_PAGE_SIZE"
        :page-route="revelationPageRoute"
      />
    </div>
  </section>
</template>
